import apiFetch from './apiFetch';

export const CRM_VIEW_BLOCKED_MESSAGE = 'Fortnox connection required to view CRM data';
export const CRM_DISABLED_MESSAGES = new Set([
  'CRM is disabled for this tenant',
  'CRM module is disabled',
]);
export const CRM_CLOSED_ACTIVITY_MESSAGE = 'Closed activities cannot be edited';

function buildRequestError(defaultMessage, status, data) {
  const error = new Error(data?.error || defaultMessage);
  error.status = status;
  error.data = data;
  return error;
}

async function parseResponseData(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? { error: text } : null;
}

function encodeCustomerNumber(customerNumber) {
  return encodeURIComponent(String(customerNumber ?? '').trim());
}

function encodeContactId(contactId) {
  return encodeURIComponent(String(contactId ?? '').trim());
}

function encodeActivityId(activityId) {
  return encodeURIComponent(String(activityId ?? '').trim());
}

export function isCrmDisabledError(error) {
  return error?.status === 403 && CRM_DISABLED_MESSAGES.has(error?.message);
}

export function isCrmViewBlockedError(error) {
  return error?.status === 409 && error?.message === CRM_VIEW_BLOCKED_MESSAGE;
}

export function isCrmWriteBlockedError(error) {
  return error?.status === 409 && String(error?.message ?? '').startsWith('Fortnox connection required');
}

export function isCrmClosedActivityError(error) {
  return error?.status === 409 && error?.message === CRM_CLOSED_ACTIVITY_MESSAGE;
}

export async function fetchCrmDashboard() {
  const response = await apiFetch('/api/crm/dashboard');
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load the CRM dashboard.', response.status, data);
  }

  return data ?? {};
}

export async function fetchCrmStats(filters = {}) {
  const query = new URLSearchParams();
  const filterKeys = [
    'assigneeId',
    'createdFrom',
    'createdTo',
    'dueFrom',
    'dueTo',
    'activityType',
    'state',
  ];

  filterKeys.forEach((key) => {
    const value = String(filters[key] ?? '').trim();

    if (value) {
      query.set(key, value);
    }
  });

  const response = await apiFetch(`/api/crm/stats${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load CRM stats.', response.status, data);
  }

  return data ?? {};
}

export async function fetchCrmCustomers() {
  const response = await apiFetch('/api/crm/customers');
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load customers.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}

export async function fetchCrmCustomer(customerNumber) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const response = await apiFetch(`/api/crm/customers/${encodedCustomerNumber}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load the customer.', response.status, data);
  }

  return data;
}

export async function updateCrmCustomerProfile(customerNumber, payload) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const response = await apiFetch(`/api/crm/customers/${encodedCustomerNumber}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to save the CRM profile.', response.status, data);
  }

  return data;
}

export async function fetchCrmCustomerContacts(customerNumber, { includeArchived = false } = {}) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const query = new URLSearchParams({
    includeArchived: includeArchived ? 'true' : 'false',
  });
  const response = await apiFetch(
    `/api/crm/customers/${encodedCustomerNumber}/contacts?${query.toString()}`
  );
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load customer contacts.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}

export async function createCrmCustomerContact(customerNumber, payload) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const response = await apiFetch(`/api/crm/customers/${encodedCustomerNumber}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to create the contact.', response.status, data);
  }

  return data;
}

export async function updateCrmCustomerContact(customerNumber, contactId, payload) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const encodedContactId = encodeContactId(contactId);
  const response = await apiFetch(
    `/api/crm/customers/${encodedCustomerNumber}/contacts/${encodedContactId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to update the contact.', response.status, data);
  }

  return data;
}

export async function fetchCrmCustomerActivities(customerNumber, { state } = {}) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const query = new URLSearchParams();
  if (state) {
    query.set('state', String(state).trim());
  }
  const response = await apiFetch(
    `/api/crm/customers/${encodedCustomerNumber}/activities${query.toString() ? `?${query.toString()}` : ''}`
  );
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load customer activities.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}

export async function createCrmCustomerActivity(customerNumber, payload) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const response = await apiFetch(`/api/crm/customers/${encodedCustomerNumber}/activities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to create the activity.', response.status, data);
  }

  return data;
}

export async function updateCrmCustomerActivity(customerNumber, activityId, payload) {
  const encodedCustomerNumber = encodeCustomerNumber(customerNumber);
  const encodedActivityId = encodeActivityId(activityId);
  const response = await apiFetch(
    `/api/crm/customers/${encodedCustomerNumber}/activities/${encodedActivityId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to update the activity.', response.status, data);
  }

  return data;
}
